(function () {
  const controllers = new WeakMap();

  class HomeCampaignDual {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-campaign-track]');
      this.slides = Array.from(root.querySelectorAll('.home-campaign-dual__panel'));
      this.mobileMedia = window.matchMedia('(max-width: 47.999em)');
      this.reduceMotionMedia = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.preloadTimer = null;
      this.slideTimer = null;
      this.settleTimer = null;
      this.resumeTimer = null;
      this.resetFrame = null;
      this.isPointerInteracting = false;
      this.pauseForInteraction = this.pauseForInteraction.bind(this);
      this.resumeAfterInteraction = this.resumeAfterInteraction.bind(this);
      this.restartAfterWheel = this.restartAfterWheel.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.syncLayout = this.syncLayout.bind(this);

      if (!this.track || this.slides.length < 2) return;

      this.root.addEventListener('pointerdown', this.pauseForInteraction, { passive: true });
      this.root.addEventListener('wheel', this.restartAfterWheel, { passive: true });
      window.addEventListener('pointerup', this.resumeAfterInteraction, { passive: true });
      window.addEventListener('pointercancel', this.resumeAfterInteraction, { passive: true });
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      this.mobileMedia.addEventListener('change', this.syncLayout);
      this.syncLayout();
    }

    destroy() {
      this.cancelAutoSlide();
      this.removeLoopClone();
      this.root.removeEventListener('pointerdown', this.pauseForInteraction);
      this.root.removeEventListener('wheel', this.restartAfterWheel);
      window.removeEventListener('pointerup', this.resumeAfterInteraction);
      window.removeEventListener('pointercancel', this.resumeAfterInteraction);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      this.mobileMedia.removeEventListener('change', this.syncLayout);
      this.root.removeAttribute('data-campaign-carousel-ready');
    }

    syncLayout() {
      this.cancelAutoSlide();
      this.track.scrollTo({ left: 0, behavior: 'auto' });

      if (!this.mobileMedia.matches) {
        this.removeLoopClone();
        return;
      }

      this.ensureLoopClone();
      this.scheduleNextSlide();
    }

    ensureLoopClone() {
      if (this.loopClone) return;

      this.loopClone = this.slides[0].cloneNode(true);
      this.loopClone.classList.add('home-campaign-dual__panel--clone');
      this.loopClone.setAttribute('aria-hidden', 'true');
      this.loopClone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      this.track.append(this.loopClone);
    }

    removeLoopClone() {
      if (!this.loopClone) return;
      this.loopClone.remove();
      this.loopClone = null;
    }

    scheduleNextSlide() {
      this.cancelAutoSlide();

      if (!this.mobileMedia.matches || !this.loopClone) return;

      const delay = Number(this.root.dataset.autoSlideDelay) || 5000;
      const currentIndex = Math.round(this.track.scrollLeft / this.track.clientWidth);
      const resetBeforeMove = currentIndex === 2;
      const nextIndex = currentIndex === 0 ? 1 : currentIndex === 1 ? 2 : 1;
      const nextImage = this.track.children[nextIndex]?.querySelector('img');

      this.preloadTimer = window.setTimeout(() => {
        if (!nextImage) return;
        nextImage.loading = 'eager';
        nextImage.fetchPriority = 'low';
        nextImage.decode().catch(() => {});
      }, Math.max(0, delay - 2000));

      this.slideTimer = window.setTimeout(() => {
        this.moveToNextSlide(nextIndex, resetBeforeMove);
      }, delay);
    }

    moveToNextSlide(nextIndex, resetBeforeMove) {
      if (!this.root.isConnected || !this.mobileMedia.matches) return;

      if (resetBeforeMove) {
        this.resetAndMoveToNextSlide(nextIndex);
        return;
      }

      this.animateToSlide(nextIndex);
    }

    animateToSlide(nextIndex) {
      this.track.scrollTo({
        left: this.track.clientWidth * nextIndex,
        behavior: this.reduceMotionMedia.matches ? 'auto' : 'smooth'
      });

      this.settleTimer = window.setTimeout(() => {
        this.scheduleNextSlide();
      }, this.reduceMotionMedia.matches ? 0 : 700);
    }

    resetAndMoveToNextSlide(nextIndex) {
      this.track.style.scrollBehavior = 'auto';
      this.track.style.scrollSnapType = 'none';
      this.track.scrollLeft = 0;
      this.track.getBoundingClientRect();

      this.resetFrame = window.requestAnimationFrame(() => {
        this.track.style.scrollBehavior = '';
        this.track.style.scrollSnapType = '';
        this.resetFrame = window.requestAnimationFrame(() => {
          this.resetFrame = null;
          this.animateToSlide(nextIndex);
        });
      });
    }

    pauseForInteraction() {
      this.isPointerInteracting = true;
      this.cancelAutoSlide();
    }

    resumeAfterInteraction() {
      if (!this.isPointerInteracting) return;
      this.isPointerInteracting = false;
      this.queueAutoSlide();
    }

    restartAfterWheel() {
      if (!this.mobileMedia.matches) return;
      this.queueAutoSlide();
    }

    handleVisibilityChange() {
      if (document.hidden) {
        this.cancelAutoSlide();
        return;
      }

      this.queueAutoSlide();
    }

    queueAutoSlide() {
      this.cancelAutoSlide();

      if (!this.root.isConnected || !this.mobileMedia.matches) return;

      this.resumeTimer = window.setTimeout(() => {
        this.scheduleNextSlide();
      }, 350);
    }

    cancelAutoSlide() {
      window.clearTimeout(this.preloadTimer);
      window.clearTimeout(this.slideTimer);
      window.clearTimeout(this.settleTimer);
      window.clearTimeout(this.resumeTimer);
      window.cancelAnimationFrame(this.resetFrame);
      this.preloadTimer = null;
      this.slideTimer = null;
      this.settleTimer = null;
      this.resumeTimer = null;
      this.resetFrame = null;
    }
  }

  function initialize(root) {
    if (root.dataset.campaignCarouselReady === 'true') return;
    root.dataset.campaignCarouselReady = 'true';
    controllers.set(root, new HomeCampaignDual(root));
  }

  document.querySelectorAll('home-campaign-dual').forEach(initialize);

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('home-campaign-dual').forEach(initialize);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('home-campaign-dual').forEach((root) => {
      controllers.get(root)?.destroy();
      controllers.delete(root);
    });
  });
})();
